/** Campaign Store
 * Used to run a campaign, save, edit and delete drafts
 */

import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import * as ct from 'countries-and-timezones';
import { cloneDeep } from 'lodash';

import msApi from '@/utils/msApi';
import type { ICompanyPO } from '@/types';
import { DATE_TIME_FORMAT_SERVER, formatDateTime } from '@/utils/strings';
import { NewCreatorSelectionModes } from '@/views/create/constants';
import { del, get, patch, post, put } from '@/utils/request';
import { uploadImages } from '@/utils/upload';

import { useBriefsStore, type BriefForm } from '@/views/create/create-campaign-v2/stores/campaignBrief';
import { useCompanyStore } from './company';
import { useConfsStore } from './confs';
import { useJobsStore } from './jobs';
import { useGroupsStore } from './groups';
import { useInfluencersStore } from './influencers';
import usePlatformAnalytics from '@/composables/usePlatformAnalytics';

export const emptyDeliverable = {
  influencer_ids: [] as string[],
  start_date: '',
  end_date: '',
  bonus_type: 'NONE',
  bonus_amount: 0,
  extra_instructions: '',
  channel: null as string | null,
  post_content_type: null as string | null,
  jobId: undefined as string | undefined,
} as const;

export const emptyCampaignForm = {
  // ---- Campaign details
  id: null as string | null,
  name: '',
  start_date: '',
  start_time: '',
  end_date: '',
  end_time: '',
  timezone: '',
  platforms: [] as string[],
  budget: 0,
  currency: '',
  subcategories: [] as string[],
  purchase_order: null as ICompanyPO | null | undefined,
  // ---- Preferences
  influencer_groups: [] as string[],
  influencer_ids: [] as string[],
  preferences: {
    gender: 'BOTH',
    age_group: [18, 100], // for compatibility with el-slider
    age_groups: [] as string[], // from conf
    income_categories: [] as string[],
    influencer_levels: [] as string[],
    locations: [] as string[],
    countries: '',
  },
  content_creator_type_ids: [] as string[],
  // ---- Campaign Brief
  campaign_objective: '',
  description: '',
  call_to_action: '',
  thumbnail: [] as any[],
  assets: [] as any[],
  handles: [] as { channel: string; text: string }[],
  hashtags: [] as { channel: string; text: string }[],
  contract_ids: [] as string[],
  contract_template_ids: [] as string[],
  selling_points: ['', ''],
  content_creation_steps: ['', ''],
  product_overview: '',
  pre_approval: false,
  advertiser_pre_approval: false,
  contract_signature_required: false,
  // ---- Deliverables
  deliverables: [] as any[],
};

export const emptyFilterForm = {
  active_statuses: [] as string[],
  platforms: [] as string[],
  channel_statuses: [] as string[],
  selectedPosting: [] as string[],
  selectedInterested: [] as string[],
  occupations: [] as string[],
  page_number: 1,
  page_size: 10,
  sortings: [] as { sort_by: string; sort_direction: string }[],
  free_text: '',
};

//---- Store ------------------------------------------------------------------------------

export const useCampaignStore = defineStore('campaign', {
  state: () => ({
    budget_min_max: null as any | null,
    channelsForCountry: null as any | null,
    influencerSelectionMode: null as NewCreatorSelectionModes | null,
    influencers: [] as any[] as any[],
    totalInfluencersFromSearch: 0,
    updatedSelectedInfluencers: [] as any[],
    influencerSearchLoading: false,
    locations: [] as string[],
    campaignForm: cloneDeep(emptyCampaignForm),
    filterForm: cloneDeep(emptyFilterForm),
    error: null as any | null,
    loading: false,
    currentStepIndex: 0,
    sidebarIsOpen: false,
    searchForm: null as any | null,
    purchaseOrders: [] as ICompanyPO[],
    influencerProfileUsage: String as any,
    briefs: { data: [] as BriefForm[], isLoading: false },
    selectedBriefId: null as string | null,
    jobs: { data: [] as any[], isLoading: false },
    selectedJobIds: new Set<string>(),
    creatorGroups: { data: [] as any[], isLoading: false },
    selectedGroupIds: new Set<string>(),
    briefName: '' as string | undefined,
    originalJobDeliverables: new Map<string, any[]>(),
    /**
     * @deprecated please use influencersStore instead
     * NOTE: this change breaks src/views/influencers/influencer-details/components/InfluencerDetailsSideBar.vue
     */
    selectedInfluencers: [] as any[],
  }),

  actions: {
    async doGetPurchaseOrders(form: { brand_id: string }) {
      try {
        const data = (await msApi.msPost('/universe/po_data', form))?.data.map((e: any) => ({ ...e, po_name: e.campaign_name }));
        this.purchaseOrders = data;
      } catch (e) {
        this.error = e;
      }
      return this.purchaseOrders;
    },

    async doGetBriefsForCampaign(form: { company_id: string }) {
      try {
        this.briefs.isLoading = true;
        this.briefs.data = (await useBriefsStore().doGetCampaignBrief(form.company_id))?.data ?? [];
      } finally {
        this.briefs.isLoading = false;
      }
      return this.briefs.data;
    },
    async doGetJobsForCampaign(form: { company_id: string }) {
      try {
        this.jobs.isLoading = true;
        this.jobs.data =
          (
            await useJobsStore().doGetJobs(
              form.company_id,
              '/jobs-marketplace/campaign_jobs',
              this.campaignForm.platforms,
              this.campaignForm.currency,
            )
          )?.data ?? [];
      } finally {
        this.jobs.isLoading = false;
      }
      return this.jobs.data;
    },

    async doGetCreatorGroups(form: { company_id: string }) {
      try {
        this.creatorGroups.isLoading = true;
        this.creatorGroups.data = (await useGroupsStore().doGetGroups(true)).filter((e: any) => e.company_id == form.company_id);
      } finally {
        this.creatorGroups.isLoading = false;
      }
    },

    saveCampaignBrief(brief_id?: string) {
      // Determine the brief to use

      if (brief_id === 'custom') {
        useInfluencersStore().influencerSearchForm = {
          ...useInfluencersStore().influencerSearchForm,
          platforms: this.campaignForm.platforms,
          country: this.campaignForm.preferences.countries,
        };

        this.selectedBriefId = brief_id;
        this.selectedJobIds.clear();
        useInfluencersStore().selectedInfluencers = [];
        useInfluencersStore().unSelectedInfluencers = [];
        this.campaignForm.deliverables = [];

        return;
      }

      const currentBrief = brief_id ? this.briefs.data.find(e => e.brief_id === brief_id) : useBriefsStore().selectedBrief;
      this.selectedBriefId = brief_id ? brief_id : 'custom';

      // Destructure relevant fields from currentBrief for cleaner assignment
      const {
        platform = '',
        overview = '',
        objectives = '',
        call_to_action = '',
        hashtags,
        handles,
        key_selling_points = [],
        content_creation_steps = [],
        brief_name,
        niche,
      } = currentBrief || {};

      // Update campaignForm in a single step
      this.campaignForm = {
        ...this.campaignForm,
        platforms: Array.isArray(platform) ? [...platform] : [platform],
        product_overview: overview,
        campaign_objective: objectives,
        call_to_action,
        handles: handles ? handles.map(handle => ({ channel: typeof platform === 'string' ? platform : '', text: handle })) : [],
        hashtags: hashtags ? hashtags.map(hashtag => ({ channel: typeof platform === 'string' ? platform : '', text: hashtag })) : [],
        selling_points: key_selling_points,
        content_creation_steps,
        subcategories: niche as string[],
      };

      this.briefName = brief_name;

      // Update influencerSearchForm
      useInfluencersStore().influencerSearchForm = {
        ...useInfluencersStore().influencerSearchForm,
        platforms: Array.isArray(platform) ? [...platform] : [platform],
        country: this.campaignForm.preferences.countries,
      };

      this.selectedBriefId === 'custom' ? this.currentStepIndex++ : null;
      this.selectedJobIds.clear();
      useInfluencersStore().selectedInfluencers = [];
      useInfluencersStore().unSelectedInfluencers = [];
      this.campaignForm.deliverables = [];
    },

    resetCampaignBrief() {
      // Reset only the fields that were updated in saveCampaignBrief

      // if (this.selectedBriefId === 'custom' || typeof this.selectedBriefId === 'string') return;

      this.campaignForm = {
        ...this.campaignForm, // Keep the rest of the form data intact
        platforms: [],
        product_overview: '',
        campaign_objective: '',
        call_to_action: '',
        handles: [],
        hashtags: [],
        selling_points: ['', ''],
        content_creation_steps: ['', ''],
        subcategories: [],
        deliverables: [],
      };

      // Reset influencerSearchForm for platforms and country
      useInfluencersStore().influencerSearchForm = {
        ...useInfluencersStore().influencerSearchForm,
        platforms: [],
        country: '',
      };

      this.briefName = '';

      // Clear selected brief ID
      this.selectedBriefId = null;
    },

    //* Influencer search -------------------------------------------------------------------
    async doSearchInfluencers() {
      const confsStore = useConfsStore();
      const filterPlatforms = this.campaignForm.platforms;
      const tempForm = {
        age_from: this.campaignForm.preferences.age_group[0].toString(),
        age_to: this.campaignForm.preferences.age_group[1].toString(),
        content_creator_type_ids: this.campaignForm.content_creator_type_ids,
        locations:
          this.campaignForm.preferences.locations.length > 0
            ? this.campaignForm.preferences.locations
            : this.cities.map((e: any) => e.value),
        channels_with_levels: filterPlatforms.map((platform: string) => {
          return {
            channel: platform,
            levels:
              this.campaignForm.preferences.influencer_levels.length > 0
                ? this.campaignForm.preferences.influencer_levels
                : confsStore.influencerLevels.map((e: any) => e.value),
          };
        }),
        gender: this.campaignForm.preferences.gender,
        subcategories: [
          ...this.campaignForm.subcategories.map(e => ({ enum_value: e, subcategory_type: 'POSTING' })),
          ...this.filterForm.occupations.map(e => ({ enum_value: e, subcategory_type: 'OCCUPATION' })),
        ],
        influencer_group_ids: this.campaignForm.influencer_groups,
        page_number: this.filterForm.page_number,
        page_size: this.filterForm.page_size,
        income_categories: this.campaignForm.preferences.income_categories,
        sortings: this.filterForm.sortings,
        free_text: this.filterForm.free_text,
      };
      this.searchForm = tempForm;
      try {
        this.influencerSearchLoading = true;
        const data = await post('/influencers/search_for_campaign', tempForm);
        const influencers_list = data.influencers_for_search ? data.influencers_for_search : [];
        this.influencers = influencers_list;
        localStorage.setItem('influencers_list', JSON.stringify(influencers_list));
        this.totalInfluencersFromSearch = data.total_search_results ?? 0;

        const infStore = useInfluencersStore();

        // Update selected influencer data
        infStore.selectedInfluencers = infStore.selectedInfluencers.map(e => {
          const newData = this.influencers.find((newInf: any) => newInf.id === e.id);
          const index = this.influencers.findIndex((newInf: any) => newInf.id === e.id);
          // Update the returned data
          if (newData) {
            e.matching_channels = Array.from(new Set([...newData.matching_channels, ...e.matching_channels]));
            this.influencers[index].matching_channels = e.matching_channels;
            e.matching_channels_with_levels = e.matching_channels_with_levels.concat(newData.matching_channels_with_levels);
            this.influencers[index].matching_channels_with_levels = e.matching_channels_with_levels;
            e.matching_combined_followers_count = newData.matching_combined_followers_count;
          }

          return e;
        });
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.influencerSearchLoading = false;
      }
    },
    async doSelectInfluencersByIds(ids: string[]) {
      this.error = null;

      try {
        const page_size = 50;
        const data = [];
        // recursive fetch
        for (let i = 0; i < Math.ceil(ids.length / page_size); i++) {
          const res = await post('/influencers/find_by_ids', {
            ids,
            page_size,
            page_number: i + 1,
          });
          data.push(...res.influencers);
        }

        const infStore = useInfluencersStore();

        infStore.selectedInfluencers = data.map(influencer => {
          return {
            ...influencer,
            matching_channels: influencer.channels,
            matching_channels_with_levels: influencer.channels_levels,
          };
        });
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    //* Campaign creation, draft creation and update ----------------------------------------------------------------

    async doPostCampaign(form: any, isDraft: boolean = false) {
      const confsStore = useConfsStore();
      const infStore = useInfluencersStore();

      const campaignForm = {
        ...form,
        influencer_ids: infStore.selectedInfluencers.map(e => e.id),
        preferences: {
          ...form.preferences,
          countries: [form.preferences.countries],
          age_groups: confsStore.conf.age_groups.map((e: any) => e.value), // TODO BE needs to accept age range
          influencer_levels:
            this.campaignForm.preferences.influencer_levels.length > 0
              ? this.campaignForm.preferences.influencer_levels
              : confsStore.influencerLevels.map((e: any) => e.value),
        },
        bonus_type: 'NONE',
        bonus_amount: 0,
        selling_points: form.selling_points.map((description: string, row_order: number) => ({
          description,
          row_order: row_order + 1,
        })),
        content_creation_steps: form.content_creation_steps.map((description: string, row_order: number) => ({
          description,
          row_order: row_order + 1,
        })),
        tasks: [
          ...this.computedDeliverablesPlatforms.map((channel: string) => ({
            channel,
            hashtags: this.campaignForm.hashtags.filter((e: any) => e.channel === channel).map((e: any) => e.text),
            handles: this.campaignForm.handles.filter((e: any) => e.channel === channel).map((e: any) => e.text),
            must_haves: [],
            should_not_haves: [],
            description: '',
            files: [],
            subtasks: this.campaignForm.deliverables
              .filter(e => e.channel === channel)
              .map(deliverable => ({
                ...deliverable,
                start_date: this.dateCast(deliverable.start_date, isDraft ? '00:00' : form.start_time),
                end_date: this.dateCast(deliverable.end_date, isDraft ? '00:00' : form.end_time),
                currency: this.campaignForm.currency,
              })),
          })),
        ],
        subcategories: [...this.campaignForm.subcategories, ...this.filterForm.occupations],
      };

      // Convert DD/MM/YYYY to YYYY-MM-DD before calling dateCast
      const convertedStartDate = dayjs(form.start_date, 'DD/MM/YYYY').format('YYYY-MM-DD');
      const convertedEndDate = dayjs(form.end_date, 'DD/MM/YYYY').format('YYYY-MM-DD');

      campaignForm.start_date = this.dateCast(convertedStartDate, form.start_time);
      campaignForm.end_date = this.dateCast(convertedEndDate, form.end_time);

      //! Boolean flip for pre-approval
      campaignForm.skip_pre_approval = !form.pre_approval;
      campaignForm.advertiser_skip_pre_approval = !form.advertiser_pre_approval;
      campaignForm.contract_signature_required = form.contract_signature_required;

      delete campaignForm.start_time;
      delete campaignForm.end_time;
      delete campaignForm.deliverables;
      delete campaignForm.hashtags;
      delete campaignForm.handles;
      delete campaignForm.platforms;

      const { group } = form;
      if (group) {
        campaignForm.influencer_groups = [group.id];
      }
      delete campaignForm.group;

      const files = campaignForm.thumbnail.concat(campaignForm.assets);
      delete campaignForm.thumbnail;
      delete campaignForm.assets;

      // if (form.contract_template_ids.length === 0) {
      //   campaignForm.contract_signature_required = false;
      // }

      let data: any;
      try {
        this.error = null;
        this.loading = true;
        if (!isDraft) {
          // RUN CAMPAIGN
          data = await post('/campaigns', campaignForm);
          const companyStore = useCompanyStore();
          companyStore.campaigns?.push({ ...data, status: 'Upcoming' });
          if (files.length > 0) {
            uploadImages(files, 'campaign_id', data.id, '/upload/campaign/logo');
          }
        } else {
          // SAVE DRAFT
          campaignForm.start_date = null;
          campaignForm.end_date = null;
          // Update draft draft
          if (form.id) {
            data = await put(`/campaign_drafts/${form.id}`, campaignForm);
          } else {
            // Create new draft
            data = await post('/campaign_drafts', campaignForm);
          }
        }
      } catch (e: any) {
        this.error = e;
        this.loading = false;
        throw e;
      }
      this.loading = false;
      return data;
    },
    async doGetCampaign(campaign_id: string) {
      this.error = null;
      let c = {};
      try {
        const campaign = await get(`/campaigns/${campaign_id}`);
        const stats = await get(`/campaigns/${campaign_id}/stats`);
        const { budget_spent, influencers_allocated, posts_under_review, posts_verified, total_posts_count, verified_content_percent } =
          stats;
        c = {
          ...campaign,
          budget_spent,
          influencers_allocated,
          posts_under_review,
          posts_verified,
          total_posts_count,
          verified_content_percent,
        };
      } catch (e) {
        this.error = null;
      }
      return c;
    },
    async doGetCampaignDraft(draft_id: string) {
      this.error;
      let ret = {};
      try {
        ret = await get(`/campaign_drafts/${draft_id}`);
      } catch (e) {
        this.error = e;
      }
      return ret;
    },

    async doDeleteDraft(draftId: string) {
      const { trackEvent } = usePlatformAnalytics();

      trackEvent({
        event_name: 'CampaignDeleted',
        metadata: {
          draft_id: draftId,
        },
      });

      this.error = null;
      try {
        await del(`/campaign_drafts/${draftId}`);
      } catch (e) {
        this.error = e;
      }
    },

    dateCast(date: string, time: string) {
      let dateStr = `${dayjs(date, 'YYYY-MM-DD').format('DD MMM YYYY')} ${dayjs(time, 'HH:mm').format('HH:mm')}`;
      dateStr = dayjs(dateStr, 'DD MMM YYYY HH:mm').format('YYYY MM DD HH:mm ZZ');
      return dayjs(dateStr, 'YYYY MM DD HH:mm ZZ').format(`YYYY-MM-DDTHH:mm:ss`) + this.timezone;
    },
    updateSelectionMode(mode?: string) {
      useInfluencersStore().selectedInfluencers = [];
      this.campaignForm.influencer_groups = [];
      this.campaignForm.deliverables = [];
      mode;
    },

    async updateCountry(countryCode: ct.CountryCode) {
      this.campaignForm.preferences.locations = [];
      useInfluencersStore().selectedInfluencers = [];
      this.campaignForm.deliverables = [];
      await this.updateCountryMetadata(countryCode);
    },
    async updateCountryMetadata(countryCode: ct.CountryCode) {
      const confsStore = useConfsStore();
      this.campaignForm.timezone = ct.getTimezonesForCountry(countryCode)[0].utcOffsetStr.replace(':', '') ?? '';
      const currency = await confsStore.doGetCurrentCurrencyByCountry(countryCode);
      this.campaignForm.currency = currency.value;
      this.budget_min_max = await confsStore.doGetBudgetMinMaxByCurrency(currency.value);
      this.campaignForm.budget = this.budget_min_max.min_budget;
      this.channelsForCountry = await confsStore.doGetChannelsForCountry(countryCode);
    },

    calculateDeliverableBudget(deliverable: any, influencers?: any[]): number {
      let total = 0;
      const { channel, bonus_type, bonus_amount, influencer_ids } = deliverable;

      if (!influencer_ids) return 0;

      const infStore = useInfluencersStore();

      // For FIXED bonus type, return only the fixed amount
      if (bonus_type === 'FIXED') {
        return bonus_amount * influencer_ids.length;
      }

      // Calculate post amounts according to influencer level and channel
      influencer_ids.forEach((id: number) => {
        const influencer = (influencers || infStore.selectedInfluencers).find(e => e.id === id);

        const influencer_level = influencer.matching_channels_with_levels.find((e: any) => e.channel === channel).level;
        const channelConfig = this.channelsForCountry.find((e: any) => e.channel === channel && e.level === influencer_level);

        if (!channelConfig) {
          return;
        }

        const { amount } = channelConfig;
        total += amount;
      });

      // For EXTRA bonus type, add bonus to the calculated total
      if (bonus_type === 'EXTRA') {
        return total + bonus_amount * influencer_ids.length;
      }

      // Default case (no bonus or unrecognized bonus type)
      return total;
    },

    calculatePlatformFee() {
      const PLATFORM_FEE_PERCENTAGE = 30;
      if (this.influencerSelectionMode === NewCreatorSelectionModes.JOB_LISTING) {
        return (this.totalJobDeliverablesBudget * PLATFORM_FEE_PERCENTAGE) / 100;
      }
      return (this.totalDeliverablesBudget * PLATFORM_FEE_PERCENTAGE) / 100;
    },

    calculateVAT() {
      const VAT = 15;
      if (this.influencerSelectionMode === NewCreatorSelectionModes.JOB_LISTING) {
        return (this.totalJobDeliverablesBudget * VAT) / 100;
      }
      return (this.totalDeliverablesBudget * VAT) / 100;
    },

    // removes missing influencers from deliverables & deliverables with empty inf array
    cleanUpDeliverables(newSelectedInfluencers?: any[]) {
      if (!newSelectedInfluencers) newSelectedInfluencers = useInfluencersStore().selectedInfluencers;
      if (this.influencerSelectionMode === NewCreatorSelectionModes.SELF_SELECTION) {
        const selectedIds = newSelectedInfluencers.map(e => e.id);
        const tmpDeliverables = this.campaignForm.deliverables.map(deliverable => {
          deliverable.influencer_ids = deliverable.influencer_ids.filter((infId: string) => selectedIds.includes(infId));
          return deliverable;
        });
        this.campaignForm.deliverables = tmpDeliverables.filter((e: any) => e.influencer_ids?.length > 0);
      }
    },

    // ---- Reset store ----------------------------------------------------------------------
    reset() {
      this.budget_min_max = null as any | null;
      this.influencerSelectionMode = null;
      this.influencers = [] as any[] as any[];
      this.totalInfluencersFromSearch = 0;
      useInfluencersStore().selectedInfluencers = [] as any[];
      this.updatedSelectedInfluencers = [] as any[];
      this.influencerSearchLoading = false;
      this.locations = [] as string[];
      this.campaignForm = cloneDeep(emptyCampaignForm);
      this.filterForm = cloneDeep(emptyFilterForm);
      this.error = null as any | null;
      this.loading = false;
      this.purchaseOrders = [];
      this.currentStepIndex = 0;
      this.selectedGroupIds = new Set();
      this.selectedJobIds = new Set();
    },

    // sidebar control
    openSideBar() {
      this.sidebarIsOpen = true;
    },
    closeSideBar() {
      this.sidebarIsOpen = false;
    },

    // ---- CAMPAIGN EDITING -----------------------------------------------------------------
    async doAddTasksToCampaign(form: { campaign_id: string; tasks: { task_id: string; subtasks: any[] }[] }) {
      for (const { task_id, subtasks } of form.tasks) {
        if (subtasks.length === 0) continue;
        const _subtasks = subtasks.map((subtask: any) => ({
          ...subtask,
          campaign_id: form.campaign_id,
          campaign_task_id: task_id,
          start_date: formatDateTime(subtask.start_date, { format: DATE_TIME_FORMAT_SERVER }),
          end_date: formatDateTime(subtask.end_date, { format: DATE_TIME_FORMAT_SERVER }),
        }));

        await post(`/campaigns/${form.campaign_id}/tasks/${task_id}/subtasks`, { subtasks: _subtasks });
      }
    },
    async doUpdateSubtask(form: { campaign_id: string; task_id: string; subtask_id: string; subtask: { end_date: string } }) {
      return await patch(`/campaigns/${form.campaign_id}/tasks/${form.task_id}/subtasks/${form.subtask_id}`, {
        end_date: formatDateTime(form.subtask.end_date, { format: DATE_TIME_FORMAT_SERVER }),
      });
    },
    async doDeleteSubtask(form: { subtask_id: string; task_id: string; campaign_id: string }) {
      await del(`/campaigns/${form.campaign_id}/tasks/${form.task_id}/subtasks/${form.subtask_id}`);
    },
    async doRemoveInfluencerFromSubtask(form: { subtask_id: string; task_id: string; campaign_id: string; influencer_id: string }) {
      await del(`/campaigns/${form.campaign_id}/tasks/${form.task_id}/subtasks/${form.subtask_id}/influencers/${form.influencer_id}`);
    },
  },

  getters: {
    totalDeliverablesBudget(): number {
      const infStore = useInfluencersStore();

      if (!this.channelsForCountry) return 0;
      if (this.campaignForm.deliverables.length === 0) return 0;
      if (this.currentStepIndex === 3 && infStore.selectedInfluencers.length === 0) return 0;

      return this.campaignForm.deliverables
        .map((deliverable: any) => {
          let total = 0;
          const { channel, bonus_type, bonus_amount, influencer_ids } = deliverable;

          // calculate post amounts according to influencer level and channel
          influencer_ids.forEach((id: number) => {
            const influencer = infStore.selectedInfluencers.find(e => e.id === id);

            // Add safety checks
            if (!influencer) {
              return;
            }

            if (!influencer.matching_channels_with_levels) {
              return;
            }

            const channelLevel = influencer.matching_channels_with_levels.find((e: any) => e.channel === channel);
            if (!channelLevel) {
              return;
            }

            const influencer_level = channelLevel.level;
            const channelConfig = this.channelsForCountry.find((e: any) => e.channel === channel && e.level === influencer_level);

            if (!channelConfig) {
              return;
            }

            const { amount } = channelConfig;
            total += amount;
          });

          if (bonus_type === 'FIXED') {
            return total + influencer_ids.length * bonus_amount;
          }
          if (bonus_type === 'EXTRA') {
            return total + bonus_amount * influencer_ids.length;
          }

          return total;
        })
        .reduce((total: number, num: number) => total + num, 0);
    },

    totalJobDeliverablesBudget(state): number {
      if (!state.campaignForm.deliverables || state.campaignForm.deliverables.length === 0) return 0;

      const infStore = useInfluencersStore();

      if (!this.channelsForCountry) return 0;
      if (this.currentStepIndex === 3 && infStore.selectedInfluencers.length === 0) return 0;

      return state.campaignForm.deliverables
        .map((deliverable: any) => {
          let total = 0;
          const { channel, bonus_type, bonus_amount, influencer_ids } = deliverable;

          if (!influencer_ids || influencer_ids.length === 0) return 0;

          // For FIXED bonus type, return only the fixed amount
          if (bonus_type === 'FIXED') {
            return bonus_amount * influencer_ids.length;
          }

          // Calculate post amounts according to influencer level and channel
          influencer_ids.forEach((id: number) => {
            const influencer = infStore.selectedInfluencers.find(e => e.id === id);

            // Add safety checks
            if (!influencer) {
              return;
            }

            if (!influencer.matching_channels_with_levels) {
              return;
            }

            const channelLevel = influencer.matching_channels_with_levels.find((e: any) => e.channel === channel);
            if (!channelLevel) {
              return;
            }

            const influencer_level = channelLevel.level;
            const channelConfig = this.channelsForCountry.find((e: any) => e.channel === channel && e.level === influencer_level);

            if (!channelConfig) {
              return;
            }

            const { amount } = channelConfig;
            total += amount;
          });

          // For EXTRA bonus type, add bonus to the calculated total
          if (bonus_type === 'EXTRA') {
            return total + bonus_amount * influencer_ids.length;
          }

          // Default case (no bonus or unrecognized bonus type)
          return total;
        })
        .reduce((total: number, num: number) => total + num, 0);
    },

    cities(state) {
      const confsStore = useConfsStore();
      if (!state.campaignForm.preferences.countries) return [];
      return confsStore.getCities(state.campaignForm.preferences.countries);
    },
    timezone(): string | null {
      const tzs = ct.getTimezonesForCountry(this.campaignForm.preferences.countries);
      if (tzs) {
        return tzs[0].utcOffsetStr.replace(':', '');
      }
      return null;
    },
    /**
     * for the purpose of making the influencer filters work
     * @deprecated please use the influencers store
     */
    computedPlatforms(state) {
      if (state.influencerSelectionMode === NewCreatorSelectionModes.SELF_SELECTION) {
        return useInfluencersStore().selectedInfluencersChannels;
      } else {
        return state.campaignForm.platforms;
      }
    },
    computedDeliverablesPlatforms(state) {
      if (state.influencerSelectionMode === NewCreatorSelectionModes.SELF_SELECTION) {
        if (state.campaignForm.deliverables.length === 0) return state.campaignForm.platforms;
        else {
          return Array.from(new Set([].concat(...state.campaignForm.deliverables.map(e => e.channel))));
        }
      } else {
        return state.campaignForm.platforms;
      }
    },
  },
});
